import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { iepaProd } from "../../constants/global";
import { EVENT_TYPE } from "../../constants/appConfig";
import RibbonNavbar from "../../components/RibbonNavbar";
import SearchableDataGrid from '../../components/SearchableDataGrid';

const Adapter = () => {
  // ---------------- STATES ----------------
  const [importExport, setImportExport] = useState("");
  const [name, setName] = useState("");
  const [seededCustom, setSeededCustom] = useState("custom");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [active, setActive] = useState(true);

  const [sourceHeaders, setSourceHeaders] = useState([]);
  const [targetApp, setTargetApp] = useState("");
  const [mappings, setMappings] = useState([]);

  const [compileLoading, setCompileLoading] = useState(false);
  const [compileSuccess, setCompileSuccess] = useState(false);
  const [compileError, setCompileError] = useState("");

  const [sources, setSources] = useState([]);
  const [targetApps, setTargetApps] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [saveHeader, setSaveHeader] = useState(true);
  const [eventType, setEventType] = useState("");
  const [isFrozen, setIsFrozen] = useState(false);
  const [savedAdapters, setSavedAdapters] = useState([]);
  const [adaptersLoading, setAdaptersLoading] = useState(false);
  const [adaptersError, setAdaptersError] = useState("");
  const [compiledMappings, setCompiledMappings] = useState([]);
  const [adapterId, setAdapterId] = useState(null);
  const [headerId, setHeaderId] = useState([])
  const [nameError, setNameError] = useState("");
  const nameRef = useRef(null);
  const [nameUpdateError, setNameUpdateError] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveMessageType, setSaveMessageType] = useState("success"); // or "error"
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState("");
  const [showCompileSuccessMsg, setShowCompileSuccessMsg] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const isExport = importExport === "Export";
  const initialFormState = {
    name: "",
    startDate: "",
    mappings: [],
    headerId: null,
    nameUpdateError: false,
    successMsg: "",
    errorMsg: ""
  };


  const importExportOptions =
    EVENT_TYPE === "IMPORT"
      ? ["Import"]
      : EVENT_TYPE === "EXPORT"
        ? ["Export"]
        : ["Import", "Export"];

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const handleFreeze = async () => {
    if (!adapterId) {
      alert("Please compile before freezing.");
      return;
    }

    try {
      const response = await fetch(
        `${iepaProd.iepaAdapterSrvAPI}/adapters/freeze/${adapterId}/org/1`,
        {
          method: "POST"
        }
      );

      if (!response.ok) {
        throw new Error("Freeze failed");
      }

      // ✅ Only freeze UI after success
      setIsFrozen(true);
      setSuccessMsg("Adapter frozen successfully");

    } catch (err) {
      setErrorMsg(err.message || "Freeze failed");
    }
  };

  const validateName = () => {
    if (!name || !name.trim()) {
      setNameError("Name is required");
      return false;
    }

    setNameError("");
    return true;
  };

  useEffect(() => {
    console.log("IMPORT EXPORT VALUE:", importExport);
  }, [importExport]);

  const headerCellSx = {
    fontWeight: 700,          // 🔥 bold
    fontSize: "0.8rem",       // ⬆ bigger than body
    fontFamily: "Roboto, sans-serif",
    letterSpacing: "0.04em",
    color: "#1f2937",
    padding: "6px 8px",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap"
  };

  useEffect(() => {
    const fetchAdapters = async () => {
      setAdaptersLoading(true);
      setAdaptersError("");

      try {
        const res = await fetch(
          "https://apiservices.iepa.in:8196/adapters/active/org/1"
        );

        if (!res.ok) {
          throw new Error("Failed to fetch adapters");
        }

        const data = await res.json();
        setSavedAdapters(Array.isArray(data) ? data : []);
      } catch (err) {
        setAdaptersError(err.message);
      } finally {
        setAdaptersLoading(false);
      }
    };

    fetchAdapters();
  }, []);
  console.log('savedAdapters', savedAdapters)

  const adapterRows = savedAdapters.map((adapter, index) => ({
    id: adapter.id,              // Required for DataGrid
    sno: index + 1,
    name: adapter.name,
    startDate: adapter.startDate,
    endDate: adapter.endDate,
    status: adapter.status,      // Should already be Active
  }));


  const adapterColumns = [
    { field: "sno", headerName: "S No", width: 80 },
    { field: "name", headerName: "Adapter Name", flex: 1 },
    { field: "startDate", headerName: "Start Date", flex: 1 },
    { field: "endDate", headerName: "End Date", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) =>
        params.value ? "Active" : "Inactive",
    },
  ];

  // ================= FETCH SOURCES =================

  useEffect(() => {
    const fetchSources = async () => {
      try {
        const res = await fetch(
          `${iepaProd.iepaAdapterSrvAPI}/adapters/mapping/header/org/1`
        );
        const data = await res.json();
        setSources(Array.isArray(data) ? data : []);
      } catch (err) {
      }
    };
    fetchSources();
  }, []);

  // ================= FETCH TARGET APPS =================
  useEffect(() => {
    const fetchTargets = async () => {
      try {
        const res = await fetch(
          `${iepaProd.iepaAdapterSrvAPI}/adapters/target/active/org/1`
        );

        if (!res.ok) {
          throw new Error("Failed to fetch targets");
        }

        const data = await res.json();
        setTargetApps(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch targets", err);
        setTargetApps([]);
      }
    };

    fetchTargets();
  }, []);

  // ================= FETCH SOURCE HEADERS =================
  const fetchSourceHeaders = async (id) => {
    try {
      const res = await fetch(
        `${iepaProd.iepaAdapterSrvAPI}/adapters/mapping/header/${id}/org/1`
      );
      const data = await res.json();
      setHeaderId(data);
      const headers = Array.isArray(data?.adapterMappings)
        ? data.adapterMappings.map(item => item.sourceName)
        : [];
      setSourceHeaders(headers);
    } catch (err) {
      console.error("Failed to fetch source headers", err);
      setSourceHeaders([]);
    }
  };

  // ================= FETCH TARGET FIELDS =================
  const fetchTargetFields = async (targetName) => {
    try {
      const res = await fetch(
        `${iepaProd.iepaAdapterSrvAPI}/adapters/mapping/target/fields/${targetName}`
      );
      const data = await res.json();
      const rows = data.map((f, index) => ({
        srNo: index + 1,
        targetField: f.name,
        sourceField: "",
        rule: "",
        mandatory: f.mandatory,
        derived: f.derived,
        type: f.derived
          ? "Derived"
          : f.mandatory
            ? "Mandatory"
            : "Optional",
      }));

      setMappings(rows);
    } catch (err) {
      console.error("Failed to fetch target fields", err);
      setMappings([]);
    }
  };

  // ================= MAPPING CHANGE =================
  const handleMappingChange = (index, key, value) => {
    const updated = [...mappings];
    updated[index][key] = value;
    setMappings(updated);
  };

  // ================= COMPILE =================
  const handleCompile = async () => {
    if (!name || !name.trim()) {
      setNameUpdateError(true);

      // focus Name field
      setTimeout(() => {
        nameRef.current?.focus();
      }, 0);

      return; // ⛔ stop compile
    }

    setNameUpdateError(false);
    setCompileError("");
    setCompileSuccess(false);
    setCompiledMappings([]);

    // 🔒 Validation
    const invalid = mappings.filter(
      (m) => m.type === "Mandatory" && !m.sourceField && !m.rule
    );

    if (invalid.length > 0) {
      setCompileError(
        `Validation failed: ${invalid.length} mandatory field(s) not mapped`
      );
      return;
    }

    setCompileLoading(true);
    try {
      const requestBody = {
        id: -1,
        orgId: "1",
        createdBy: 1,
        adapterMappings: headerId.adapterMappings.map((m, index) => {
          const selectedRuleId = mappings[index]?.rule;

          return {
            id: m.id,
            destinationName: m.sourceName,
            inputType: "String",
            adapterRules: selectedRuleId
              ? { id: selectedRuleId }
              : {}
          };
        })
      };


      const response = await fetch(
        `${iepaProd.iepaAdapterSrvAPI}/adapters/mapping/update`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        throw new Error(await response.text());
      }

      // ✅ SAFELY parse response
      const text = await response.text();
      const data = text ? JSON.parse(text) : null;

      const adapterMappings = data?.adapterMappings ?? [];
      if (data?.id) {
        setAdapterId(data.id);
      }

      // ✅ THIS IS THE MOST IMPORTANT LINE
      setCompiledMappings(adapterMappings);
      setCompileSuccess(true);
      setShowCompileSuccessMsg(true);

    } catch (err) {
      setCompileError(err.message || "Compile failed");
    } finally {
      setCompileLoading(false);
    }
  };


  const handleSave = async () => {
    if (!name || !name.trim()) {
      setNameUpdateError(true);
      // setSaveMessage("Adapter name has to be updated");
      setSaveMessageType("error");

      // focus name field
      setTimeout(() => {
        nameRef.current?.focus();
      }, 0);

      return; // ⛔ DO NOT CALL API
    }

    setNameUpdateError(false);
    setSaveMessage("");
    try {

      const requestBody = {
        name,
        description: seededCustom,
        location: "location",
        maxRecords: 10,
        reversibleFlag: saveHeader,
        type: seededCustom.toUpperCase(),
        purgeInterface: saveHeader ? 1 : 0,
        orgId: "1",
        createdBy: 1,

        // ✅ BACKEND EXPECTS ONLY IDS
        // adapterMappings: (headerId || []).map(({ id }) => ({
        //   id
        // }))
        adapterMappings: Array.isArray(headerId?.adapterMappings)
          ? headerId.adapterMappings.map(item => ({ id: item.id }))
          : []
      };

      const response = await fetch(
        `${iepaProd.iepaAdapterSrvAPI}/adapters/create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        throw new Error(await response.text());
      }

      setSaveMessage("Adapter created successfully");
      setSaveMessageType("success");
    } catch (err) {
      setSaveMessage(err.message || "Save failed");
      setSaveMessageType("error");
    }
  };

  useEffect(() => {
    if (importExportOptions.length === 1) {
      setImportExport(importExportOptions[0]);
    }
  }, [importExportOptions]);

  useEffect(() => {
    setCompileSuccess(false);
  }, [mappings]);

  useEffect(() => {
    if (!showCompileSuccessMsg) return;

    const timer = setTimeout(() => {
      setShowCompileSuccessMsg(false);
    }, 3000); // hide after 3 seconds

    return () => clearTimeout(timer);
  }, [showCompileSuccessMsg]);

  const handleCancel = () => {
    // 🔹 Basic fields
    setImportExport("");
    setName("");
    setNameUpdateError(false);
    setSeededCustom("custom");
    setStartDate("");
    setEndDate("");
    setActive(true);

    // 🔹 Mapping / selection related
    setSourceId("");
    setTargetApp("");
    setSourceHeaders([]);
    setMappings([]);
    setHeaderId([]);

    // 🔹 Flags & messages
    setCompileSuccess(false);
    setCompileError("");
    setCompileLoading(false);
    setIsFrozen(false);

    // 🔹 Optional messages
    setSaveMessage("");
    setSaveMessageType("");
    setNameUpdateError(false);
    setSuccessMsg("");
    setErrorMsg("");

    // optional: focus back to name field
    setTimeout(() => {
      nameRef.current?.focus();
    }, 0);
  };

  //Fetch Rules
  useEffect(() => {
    const fetchRules = async () => {
      setRulesLoading(true);
      setRulesError("");

      try {
        const res = await fetch(
          `${iepaProd.iepaAdapterSrvAPI}/adapters/rules/all/org/1`
        );

        if (!res.ok) {
          throw new Error("Failed to fetch rules");
        }

        const data = await res.json();

        // keep only Active rules (optional but recommended)
        const activeRules = Array.isArray(data)
          ? data.filter(r => r.status === "Active")
          : [];

        setRules(activeRules);
      } catch (err) {
        setRulesError(err.message);
        setRules([]);
      } finally {
        setRulesLoading(false);
      }
    };

    fetchRules();
  }, []);

  return (
    <Box sx={{ p: 4 }}>
      {/* HEADER */}
      <Grid container alignItems="center" mb={3}>
        <Grid item xs={4}>
          <Typography variant="h4">Source Adapter</Typography>
        </Grid>
        <Grid item xs={5} textAlign="right">
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Import / Export</InputLabel>
            <Select
              value={importExport}
              label="Import / Export"
              onChange={(e) => setImportExport(e.target.value)}
            >
              {importExportOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3} textAlign="left">
           {!importExport && (
          <Box
            display="flex"
            justifyContent="flex-end" >
            <RibbonNavbar
            selectedRows={selectedRows} 
              showAdd
              showView
              showEdit
              showDelete

              onAdd={() => {
                handleCancel();
                setSelectedRows([]);
                setImportExport("Import");
              }}

              onView={() => {
                if (selectedRows.length !== 1) return;

                const selectedAdapter = savedAdapters.find(
                  (a) => a.id === selectedRows[0]
                );

                if (!selectedAdapter) return;

                setName(selectedAdapter.name);
                setStartDate(selectedAdapter.startDate);
                setEndDate(selectedAdapter.endDate);
                setActive(selectedAdapter.status);
                setAdapterId(selectedAdapter.id);

                setImportExport("Import");
              }}

              onEdit={() => {
                if (selectedRows.length !== 1) return;

                const selectedAdapter = savedAdapters.find(
                  (a) => a.id === selectedRows[0]
                );

                if (!selectedAdapter) return;

                setName(selectedAdapter.name);
                setStartDate(selectedAdapter.startDate);
                setEndDate(selectedAdapter.endDate);
                setActive(selectedAdapter.status);
                setAdapterId(selectedAdapter.id);

                setImportExport("Import");
              }}

              onDelete={() => {
                if (selectedRows.length === 0) return;
                console.log("Delete adapters:", selectedRows);
              }}

              disabledView={selectedRows.length !== 1}
              disabledEdit={selectedRows.length !== 1}
              disabledDelete={selectedRows.length === 0}
            />
          </Box>
           )}
        </Grid>
      </Grid>

      {!importExport && (
        <>
          {/* Ribbon Navbar */}
          
          <Paper sx={{ p: 3, mb: 4 }}>


            {adaptersLoading && <LinearProgress />}
            {adaptersError && <Alert severity="error">{adaptersError}</Alert>}

            {!adaptersLoading && savedAdapters.length === 0 && (
              <Typography color="text.secondary">
                No adapters found
              </Typography>
            )}

            <Box height="60vh">
              <SearchableDataGrid 
                rows={adapterRows}
                columns={adapterColumns}
                checkboxSelection
                rowSelectionModel={selectedRows}
                onRowSelectionModelChange={(newSelection) =>
                  setSelectedRows(newSelection)
                }
              />
            </Box>
          </Paper>
        </>
      )}

      {importExport && (
        <>
          {/* MASTER DETAILS */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Name"
                  required
                  value={name}
                  inputRef={nameRef}
                  error={nameUpdateError}
                  helperText={nameUpdateError ? "Adapter name has to be updated" : ""}
                  disabled={isFrozen}
                  onChange={(e) => {
                    setName(e.target.value);
                    setNameUpdateError(false); // clear message when user edits
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <RadioGroup
                  row
                  value={seededCustom}
                  onChange={(e) => setSeededCustom(e.target.value)}
                  disabled={isFrozen}
                >
                  <FormControlLabel value="seeded" control={<Radio />} label="Seeded" />
                  <FormControlLabel value="custom" control={<Radio />} label="Custom" />
                </RadioGroup>
              </Grid>

              <Grid item xs={3}>
                <TextField
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={isFrozen}

                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Grid>

              <Grid item xs={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={active}
                      onChange={(e) => setActive(e.target.checked)}
                      color="success"
                    />
                  }
                  label="Active"
                />

              </Grid>
              <Grid item xs={3}>
                {/* Fudge / Save Header Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={saveHeader}
                      onChange={(e) => setSaveHeader(e.target.checked)}
                      sx={{
                        padding: "6px",

                        /* default icon */
                        "& .MuiSvgIcon-root": {
                          borderRadius: "6px",
                        },

                        /* hover (unchecked) */
                        "&:hover .MuiSvgIcon-root": {
                          backgroundColor: "#e3f2fd",
                        },

                        /* checked */
                        "&.Mui-checked": {
                          color: "#1976d2",
                        },
                        "&.Mui-checked .MuiSvgIcon-root": {
                          backgroundColor: "#bbdefb",
                        },

                        /* hover when checked */
                        "&.Mui-checked:hover .MuiSvgIcon-root": {
                          backgroundColor: "#90caf9",
                        },
                      }}
                    />
                  }
                  label="Purge Interface"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      marginLeft: "8px",
                    },
                  }}
                  disabled={isFrozen}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* SOURCE + TARGET (Dynamic for Import / Export) */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Grid container spacing={2}>

              {/* ================= EXPORT FLOW ================= */}
              {isExport ? (
                <>
                  {/* 1️⃣ TARGET FIRST */}
                  <Grid item xs={6}>
                    <FormControl fullWidth disabled={isFrozen}>
                      <InputLabel>Target Application</InputLabel>
                      <Select
                        value={targetApp}
                        label="Target Application"
                        onChange={(e) => {
                          const selectedTarget = e.target.value;
                          setTargetApp(selectedTarget);

                          // Clear dependent values
                          setSourceId("");
                          setSourceHeaders([]);
                          setMappings([]);

                          fetchTargetFields(selectedTarget);
                        }}
                      >
                        {targetApps.map((t) => (
                          <MenuItem key={t.module || t.name} value={t.module || t.name}>
                            {t.module}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* 2️⃣ SOURCE AFTER TARGET */}
                  <Grid item xs={6}>
                    <FormControl fullWidth disabled={!targetApp || isFrozen}>
                      <InputLabel>Source Name</InputLabel>
                      <Select
                        value={sourceId}
                        label="Source Name"
                        onChange={(e) => {
                          const id = e.target.value;
                          setSourceId(id);
                          fetchSourceHeaders(id);
                        }}
                      >
                        {sources.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.uploadFileName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              ) : (
                /* ================= IMPORT FLOW (Existing Behavior) ================= */
                <>
                  {/* 1️⃣ SOURCE FIRST */}
                  <Grid item xs={6}>
                    <FormControl fullWidth disabled={isFrozen}>
                      <InputLabel>Source Name</InputLabel>
                      <Select
                        value={sourceId}
                        label="Source Name"
                        onChange={(e) => {
                          const id = e.target.value;
                          setSourceId(id);
                          setTargetApp("");
                          setSourceHeaders([]);
                          setMappings([]);
                          fetchSourceHeaders(id);
                        }}
                      >
                        {sources.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.uploadFileName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* 2️⃣ TARGET AFTER SOURCE */}
                  <Grid item xs={6}>
                    <FormControl fullWidth disabled={!sourceId || isFrozen}>
                      <InputLabel>Target Application</InputLabel>
                      <Select
                        value={targetApp}
                        label="Target Application"
                        onChange={(e) => {
                          const selectedTarget = e.target.value;
                          setTargetApp(selectedTarget);
                          fetchTargetFields(selectedTarget);
                        }}
                      >
                        {targetApps.map((t) => (
                          <MenuItem key={t.module} value={t.module}>
                            {t.module}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {/* SOURCE HEADERS GRID */}
          {sourceHeaders.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Source File Fields</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Table disabled={isFrozen}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sr No</TableCell>
                      <TableCell>Field Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody >
                    {sourceHeaders.map((h, i) => (
                      <TableRow key={i}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{h}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          )}

          {/* MAPPING SECTION */}
          {sourceId && targetApp && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Mapping Section</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    pointerEvents: isFrozen ? "none" : "auto",
                    opacity: isFrozen ? 0.6 : 1,
                  }}
                >
                  {compileError && <Alert severity="error">{compileError}</Alert>}
                  {showCompileSuccessMsg && <Alert severity="success">Compiled Successfully</Alert>}
                  {compileLoading && <LinearProgress />}

                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
                        <TableCell sx={headerCellSx}>Sr No</TableCell>
                        <TableCell sx={headerCellSx}>Target Field</TableCell>
                        <TableCell sx={headerCellSx}>Source Field</TableCell>
                        <TableCell sx={headerCellSx}>Rule</TableCell>
                        <TableCell sx={headerCellSx}>Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mappings.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.srNo}</TableCell>
                          <TableCell>{row.targetField}</TableCell>

                          <TableCell>
                            <Select
                              fullWidth
                              displayEmpty
                              value={row.sourceField || ""}
                              disabled={Boolean(row.rule)} // 🔒 disable if rule is selected
                              onChange={(e) => {
                                const value = e.target.value;

                                handleMappingChange(idx, "sourceField", value);

                                // If source selected → clear rule
                                if (value !== "") {
                                  handleMappingChange(idx, "rule", "");
                                }
                              }}
                            >
                              <MenuItem value="">
                                None
                              </MenuItem>

                              {sourceHeaders.map((h) => (
                                <MenuItem key={h} value={h}>
                                  {h}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>

                          <TableCell>
                            <FormControl fullWidth>
                              <Select
                                value={row.rule || ""}
                                displayEmpty
                                disabled={Boolean(row.sourceField)}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  handleMappingChange(idx, "rule", value);

                                  if (value !== "") {
                                    handleMappingChange(idx, "sourceField", "");
                                  }
                                }}
                                renderValue={(selected) => {
                                  if (!selected) return "None";
                                  const selectedRule = rules.find(r => r.id === selected);
                                  return selectedRule?.name || "None";
                                }}
                              >
                                <MenuItem value="">None</MenuItem>

                                {rules.map(rule => (
                                  <MenuItem key={rule.id} value={rule.id}>
                                    {rule.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>

                          <TableCell>
                            <Select
                              fullWidth
                              value={row.type}
                              disabled={row.mandatory}
                              onChange={(e) =>
                                handleMappingChange(idx, "type", e.target.value)
                              }
                            >
                              <MenuItem value="Mandatory">Mandatory</MenuItem>
                              <MenuItem value="Optional">Optional</MenuItem>
                              <MenuItem value="Derived">Derived</MenuItem>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* ACTION BUTTONS */}
          {nameUpdateError && (
            <Typography
              sx={{
                mb: 1.5,
                color: "#d32f2f",
                fontSize: "0.875rem",
                fontWeight: 500
              }}
            >
              Adapter name has to be updated
            </Typography>
          )}

          {saveMessage && (
            <Typography
              severity={saveMessageType}
              sx={{
                mb: 2,
                fontWeight: 600,
                color: saveMessageType === "success" ? "green" : "red"
              }}
            >
              {saveMessage}
            </Typography>
          )}
          <Box mt={4} display="flex" justifyContent="space-between">

            <Box display="flex" gap={2}>

              <Button variant="contained" onClick={handleSave}>Save</Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  backgroundColor: "#ffffff",
                  color: "#000000",
                  borderColor: "#000000",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    borderColor: "#000000"
                  }
                }}
              >
                Cancel
              </Button>
            </Box>
            <Box display="flex" gap={2}>
              <Button variant="contained" onClick={handleCompile} color={compileSuccess ? "success" : "primary"} disabled={compileLoading}>
                Compile
              </Button>
              <Button
                variant="contained"
                disabled={!compileSuccess}
                onClick={handleFreeze}
              >
                Freeze
              </Button>
            </Box>
          </Box>


        </>
      )}
    </Box>
  );
};

export default Adapter;
